import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaEye,
  FaExternalLinkAlt,
  FaChartLine,
  FaUsers,
  FaRocket,
  FaFilter,
} from "react-icons/fa";

const PortfolioSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 10% 20%,
      rgba(102, 126, 234, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 90% 80%,
      rgba(118, 75, 162, 0.05) 0%,
      transparent 50%
    );
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.3);
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 50%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #b8b8b8;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 60px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const FilterTab = styled(motion.button)`
  background: ${(props) =>
    props.active
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "rgba(255, 255, 255, 0.05)"};
  color: ${(props) => (props.active ? "white" : "#b8b8b8")};
  border: 1px solid
    ${(props) => (props.active ? "transparent" : "rgba(255, 255, 255, 0.1)")};
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${(props) =>
      props.active
        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        : "rgba(255, 255, 255, 0.1)"};
    color: white;
  }
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const PortfolioCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 25px;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    border-color: rgba(102, 126, 234, 0.3);
    box-shadow: 0 20px 40px rgba(102, 126, 234, 0.1);
  }
`;

const CardImage = styled.div`
  height: 250px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-image: url(${(props) => props.bgImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.8) 0%,
      rgba(118, 75, 162, 0.8) 100%
    );
    transition: opacity 0.3s ease;
  }

  ${PortfolioCard}:hover &::before {
    opacity: 0.3;
  }

  span {
    position: relative;
    z-index: 2;
    display: none;
  }
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${PortfolioCard}:hover & {
    opacity: 1;
  }
`;

const OverlayButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const CardContent = styled.div`
  padding: 30px;
`;

const CardCategory = styled.div`
  color: #667eea;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 15px;
`;

const CardDescription = styled.p`
  color: #b8b8b8;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const CardStats = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #b8b8b8;
  font-size: 0.9rem;
`;

const CardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  border: 1px solid rgba(102, 126, 234, 0.2);
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: block;
  margin: 0 auto;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const Portfolio = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [activeFilter, setActiveFilter] = useState("all");

  const filters = [
    { id: "all", label: "全部案例", icon: <FaFilter /> },
    { id: "advertising", label: "广告投放", icon: <FaRocket /> },
    { id: "seo", label: "SEO优化", icon: <FaChartLine /> },
    { id: "social", label: "社交媒体", icon: <FaUsers /> },
  ];

  const portfolioItems = [
    {
      id: 1,
      category: "advertising",
      title: "某知名电商平台广告投放优化",
      description:
        "通过精准的广告投放策略，帮助客户实现300%的ROI提升，月销售额增长500万+",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      stats: [
        { icon: <FaChartLine />, label: "ROI提升300%" },
        { icon: <FaUsers />, label: "覆盖用户100万+" },
      ],
      tags: ["Google Ads", "Facebook Ads", "数据分析", "ROI优化"],
    },
    {
      id: 2,
      category: "seo",
      title: "制造业企业官网SEO优化",
      description:
        "通过全面的SEO优化策略，帮助客户网站排名提升至首页，流量增长200%",
      image:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      stats: [
        { icon: <FaChartLine />, label: "流量增长200%" },
        { icon: <FaUsers />, label: "关键词排名首页" },
      ],
      tags: ["SEO优化", "关键词排名", "内容营销", "技术优化"],
    },
    {
      id: 3,
      category: "social",
      title: "美妆品牌社交媒体营销",
      description:
        "通过创意内容制作和KOL合作，帮助品牌在社交媒体上获得1000万+曝光",
      image:
        "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      stats: [
        { icon: <FaUsers />, label: "曝光量1000万+" },
        { icon: <FaChartLine />, label: "互动率提升150%" },
      ],
      tags: ["社交媒体", "KOL合作", "内容创作", "品牌营销"],
    },
    {
      id: 4,
      category: "advertising",
      title: "金融科技公司移动端推广",
      description:
        "通过移动端广告投放和ASO优化，帮助APP下载量增长500%，用户活跃度提升300%",
      image:
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      stats: [
        { icon: <FaRocket />, label: "下载量增长500%" },
        { icon: <FaUsers />, label: "活跃用户300%+" },
      ],
      tags: ["移动端推广", "ASO优化", "APP推广", "用户增长"],
    },
    {
      id: 5,
      category: "seo",
      title: "教育机构官网SEO项目",
      description:
        "通过内容优化和链接建设，帮助教育机构官网在搜索引擎中获得更好的排名",
      image:
        "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      stats: [
        { icon: <FaChartLine />, label: "排名提升至首页" },
        { icon: <FaUsers />, label: "咨询量增长180%" },
      ],
      tags: ["教育行业", "内容优化", "链接建设", "排名提升"],
    },
    {
      id: 6,
      category: "social",
      title: "餐饮连锁品牌社交媒体运营",
      description:
        "通过创意视频制作和社群运营，帮助餐饮品牌在社交媒体上建立强大的品牌影响力",
      image:
        "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      stats: [
        { icon: <FaUsers />, label: "粉丝增长200%" },
        { icon: <FaChartLine />, label: "订单转化率提升120%" },
      ],
      tags: ["视频制作", "社群运营", "品牌建设", "转化优化"],
    },
  ];

  const filteredItems =
    activeFilter === "all"
      ? portfolioItems
      : portfolioItems.filter((item) => item.category === activeFilter);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <PortfolioSection id="portfolio">
      <BackgroundPattern />
      <Container ref={ref}>
        <SectionHeader>
          <SectionBadge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <FaEye />
            成功案例
          </SectionBadge>

          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            我们的成功案例
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            每一个案例都是我们专业能力的体现，为客户创造真正的价值
          </SectionSubtitle>
        </SectionHeader>

        <FilterTabs>
          {filters.map((filter) => (
            <FilterTab
              key={filter.id}
              active={activeFilter === filter.id}
              onClick={() => setActiveFilter(filter.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {filter.icon}
              {filter.label}
            </FilterTab>
          ))}
        </FilterTabs>

        <AnimatePresence mode="wait">
          <PortfolioGrid
            key={activeFilter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {filteredItems.map((item, index) => (
              <PortfolioCard
                key={item.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <CardImage bgImage={item.image}>
                  <span>{item.image}</span>
                  <CardOverlay>
                    <OverlayButton>
                      <FaEye />
                    </OverlayButton>
                    <OverlayButton>
                      <FaExternalLinkAlt />
                    </OverlayButton>
                  </CardOverlay>
                </CardImage>

                <CardContent>
                  <CardCategory>{item.category}</CardCategory>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>

                  <CardStats>
                    {item.stats.map((stat, statIndex) => (
                      <StatItem key={statIndex}>
                        {stat.icon}
                        {stat.label}
                      </StatItem>
                    ))}
                  </CardStats>

                  <CardTags>
                    {item.tags.map((tag, tagIndex) => (
                      <Tag key={tagIndex}>{tag}</Tag>
                    ))}
                  </CardTags>
                </CardContent>
              </PortfolioCard>
            ))}
          </PortfolioGrid>
        </AnimatePresence>

        <CTAButton
          onClick={() => scrollToSection("contact")}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          查看更多案例
        </CTAButton>
      </Container>
    </PortfolioSection>
  );
};

export default Portfolio;
